import { defineMessages, type IntlShape } from 'react-intl';

import type {
	ExtensionManifest,
	ExtensionModule,
} from '@atlaskit/editor-common/extensions/extension-manifest';
import type { EditorActions } from '@atlaskit/editor-core/actions';

import {
	slashRemixPlaceholderKey,
	type SlashRemixPlaceholderAction,
} from '@atlassian/editor-plugin-malleable-ui/slash-remix-placeholder-key';

const messages = defineMessages({
	manifestTitle: {
		id: 'fabric-extension-lib.remix-slash.title',
		defaultMessage: 'Remix',
		description: 'Title of the Remix slash command extension',
	},
	barChartTitle: {
		id: 'fabric-extension-lib.remix-slash.bar-chart.title',
		defaultMessage: 'Bar chart',
		description: 'Title of the bar chart quick insert command in Confluence',
	},
	barChartDescription: {
		id: 'fabric-extension-lib.remix-slash.bar-chart.description',
		defaultMessage: 'Create a bar chart with Rovo',
		description: 'Description of the bar chart quick insert command in Confluence',
	},
});

const commands = [
	{
		format: 'bar-chart' as const,
		key: 'bar-chart',
		title: messages.barChartTitle,
		description: messages.barChartDescription,
		keywords: ['bar-chart', 'barchart', 'bar chart'],
		category: 'data-and-charts' as const,
		priority: 500,
		// eslint-disable-next-line import/dynamic-import-chunkname
		icon: () => import('@atlaskit/icon/core/chart-bar'),
	},
];

export const createRemixSlashManifest = (
	intl: IntlShape,
	editorActions: EditorActions,
): ExtensionManifest => {
	const quickInsert: ExtensionModule[] = commands.map((command) => ({
		key: command.key,
		title: intl.formatMessage(command.title),
		description: intl.formatMessage(command.description),
		keywords: command.keywords,
		category: command.category,
		priority: command.priority,
		icon: command.icon,
		action: async () => {
			await Promise.resolve();
			const view = editorActions._privateGetEditorView();
			view?.dispatch(
				view.state.tr.setMeta(slashRemixPlaceholderKey, {
					type: 'open',
					format: command.format,
				} satisfies SlashRemixPlaceholderAction),
			);
		},
	}));

	return {
		type: 'com.atlassian.confluence.macro.core',
		key: 'remix-slash',
		title: intl.formatMessage(messages.manifestTitle),
		icons: {
			'48': async () => {
				// eslint-disable-next-line import/dynamic-import-chunkname
				const { RovoIcon } = await import('@atlaskit/logo/rovo/icon');
				return RovoIcon;
			},
		},
		modules: { quickInsert },
	};
};
